import { isEqual } from 'lodash-es'
import type {
    AlertState,
    Authentication,
    MyWebcamState,
    MyWebcamStatePayload,
    SessionState,
    StateTokens,
    TipDefaults,
    WebcamPublisherPayload,
} from '../../types/mfc'
import { appAction } from '../actions/workerActions'
import { AppAction, AppStateType } from '../constants/enums'
import { BROADCASTING_STATE } from '../constants/myWebcam'
import { pubSubAppState } from './pubSub'
export { AppStateType, pubSubAppState }

export type IApp = {
    authenticated?: boolean
    darkMode?: boolean
    isMobile?: boolean
    isIOS?: boolean
    isIpad?: boolean
    maintenanceMode?: boolean
    passcode?: string
    nsfw?: number
}

export type AppState = {
    alerts: Record<number, AlertState>
    app: IApp
    authentication: Authentication
    myWebcam: MyWebcamState
    session: SessionState
    tipDefaults: TipDefaults
    tokens: StateTokens
}

export const defaultAPPSTATE = (): AppState => ({
    alerts: {},
    app: {
        authenticated: undefined,
        darkMode: undefined,
        isMobile: undefined,
        isIOS: undefined,
        isIpad: undefined,
        passcode: undefined,
        nsfw: undefined,
    },
    authentication: {},
    myWebcam: {
        broadcasting: BROADCASTING_STATE.UNINITIALIZED,
        isVideoPublisherReady: false,
        isWebcamAvailable: true,
    },
    session: {},
    tipDefaults: {
        anonymousTip: false,
        publicComment: true,
        publicTip: true,
    },
    tokens: {},
})

export const APPSTATE: AppState = {
    ...defaultAPPSTATE(),
}

if (typeof window !== 'undefined') {
    window.__MFC_APP_APPSTATE__ = APPSTATE
}

export const setAppState = <T extends keyof AppState>(
    key: T,
    appState: AppState[T],
    immediate = true,
) => {
    if (!key) throw new Error('[setAppState] key is required')
    if (isEqual(APPSTATE[key], appState)) return
    APPSTATE[key] = appState
    pubSubAppState.emit(key, appState, immediate)
}

export const updateAppState = <T extends keyof AppState>(
    key: T,
    appState: Partial<AppState[T]> = {},
    immediate = true,
) => {
    if (!key) throw new Error('[updateAppState] key is required')
    setAppState(key, { ...APPSTATE[key], ...appState }, immediate)
}

export function sendPublishCam(payload: WebcamPublisherPayload) {
    appAction(AppAction.PUBLISH_WEBCAM, payload)
}

export function resumeSocketConnection() {
    appAction(AppAction.RESUME_SOCKET_CONNECTION, {})
}

export function pauseSocketConnection() {
    appAction(AppAction.PAUSE_SOCKET_CONNECTION, {})
}

export function notifyWorkerOfBrowserReload() {
    appAction(AppAction.BROWSER_RELOAD, {})
}

export function resetBroadcastingState() {
    updateAppState(AppStateType.MYWEBCAM, {
        broadcasting: BROADCASTING_STATE.UNINITIALIZED,
    })
}

export function updateMyWebcamState(payload: MyWebcamStatePayload) {
    if (typeof payload.broadcasting === 'object') {
        const { state: broadcastingState } = payload.broadcasting
        const broadcasting =
            broadcastingState === 0
                ? BROADCASTING_STATE.ON
                : BROADCASTING_STATE.OFF
        updateAppState(AppStateType.MYWEBCAM, { broadcasting })
        return
    } else if (payload.isVideoPublisherReady) {
        updateAppState(AppStateType.MYWEBCAM, {
            ...payload,
            broadcasting: BROADCASTING_STATE.OFF,
        })
        return
    } else if (payload.error) {
        const { error } = payload
        updateAppState(AppStateType.MYWEBCAM, {
            error,
            broadcasting: BROADCASTING_STATE.ERROR,
        })
        return
    }
    const myWebcamState: Omit<MyWebcamState, 'broadcasting'> = payload
    updateAppState(AppStateType.MYWEBCAM, myWebcamState)
}

if (typeof window !== 'undefined') {
    window.__MFC_APP_SOCKET__ = {
        pause: pauseSocketConnection,
        resume: resumeSocketConnection,
    }
}
